import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Play, Lock, CheckCircle, Clock, Users, Star, 
  BookOpen, Crown, Award, Download, Share2, Heart
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import { MathRenderer } from '../../math/MathRenderer';

export const CourseView = ({ bookId, user, courseId, onNavigate }) => {
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    loadCourseData();
  }, [courseId, user]);

  const loadCourseData = async () => {
    try {
      setLoading(true);
      
      // Load course details
      const courseData = await courseService.getCourse(bookId, courseId);
      setCourse(courseData);

      // Load lessons
      const lessonsData = await courseService.getLessons(bookId, courseId);
      setLessons(lessonsData);

      // Load user enrollment and progress if logged in
      if (user) {
        const [enrollmentData, progressData] = await Promise.all([
          courseService.getUserEnrollment(user.uid, bookId, courseId),
          courseService.getUserProgress(user.uid, bookId, courseId)
        ]);
        
        setEnrollment(enrollmentData);
        setProgress(progressData);
      }
      
    } catch (error) {
      console.error('Error loading course:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      alert('Please sign in to enroll in courses');
      navigate('/login');
      return;
    }

    if (course.isPremium && !course.price) {
      alert('Payment required for premium courses');
      return;
    }

    try {
      setEnrolling(true);
      await courseService.enrollUser(user.uid, bookId, courseId, course.isPremium ? 'paid' : 'free');
      await loadCourseData();
    } catch (error) {
      console.error('Error enrolling:', error);
      alert(error.message || 'Failed to enroll in course');
    } finally {
      setEnrolling(false);
    }
  };

  const canAccessLesson = (lesson) => {
    if (!enrollment) return lesson.isPreview;
    return true;
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completedLessons?.includes(lessonId) || false;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return Play;
      case 'text': return BookOpen;
      case 'quiz': return Award;
      default: return BookOpen;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Course not found</h2>
          <button
            onClick={() => onNavigate('discover')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Back to courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => onNavigate('discover')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Discovery</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Course Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">{course.title}</h1>
                  <div className="text-gray-600 text-md leading-relaxed mb-6">
                    <MathRenderer 
                      content={course.description || ''}
                      enableMarkdown={true}
                      enableMath={true}
                      className="text-gray-600"
                    />
                  </div>
                  
                  <div className="flex items-center flex-wrap gap-6 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{course.enrollmentCount || 0} enrolled</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{formatDuration(course.duration)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      <span>{lessons.length} lessons</span>
                    </div>
                    {course.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="font-semibold text-gray-900">{course.rating.toFixed(1)}</span>
                        <span>({course.ratingCount || 0} ratings)</span>
                      </div>
                    )}
                  </div>
                </div>

                {course.isPremium && (
                  <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">
                    <Crown className="w-4 h-4" />
                    <span className="text-sm font-medium">Premium</span>
                  </div>
                )}
              </div>

              {/* Enrollment Action */}
              {!enrollment ? (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      {course.isPremium ? (
                        <div className="text-2xl font-bold text-red-600">
                          R{course.price}
                        </div>
                      ) : (
                        <div className="text-2xl font-bold text-green-600">
                          Free Course
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleEnroll}
                      disabled={enrolling}
                      className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {enrolling ? 'Enrolling...' : 'Enroll Now'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <div className="font-semibold text-gray-900">Enrolled</div>
                        <div className="text-sm text-gray-600">
                          {progress?.completionPercentage || 0}% complete
                        </div>
                      </div>
                    </div>
                    {progress?.completionPercentage > 0 && (
                      <div className="w-48">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all"
                            style={{ width: `${progress.completionPercentage}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* What You'll Learn */}
            {course.learningObjectives && course.learningObjectives.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">What You'll Learn</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {course.learningObjectives.map((objective, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{objective}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Course Content */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Course Content</h2>
              <div className="text-sm text-gray-600 mb-6">
                {lessons.length} lessons • {formatDuration(course.duration)} total
              </div>

              <div className="space-y-2">
                {lessons.map((lesson, index) => {
                  const Icon = getLessonIcon(lesson.type);
                  const isCompleted = isLessonCompleted(lesson.id);
                  const canAccess = canAccessLesson(lesson);
                  const isLocked = !canAccess;

                  return (
                    <button
                      key={lesson.id}
                      onClick={() => canAccess && onNavigate('lesson', { courseId, lessonId: lesson.id })}
                      disabled={isLocked}
                      className={`w-full flex items-center gap-4 p-4 rounded-lg border transition-all ${
                        isLocked 
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer'
                      }`}
                    >
                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {isCompleted ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : isLocked ? (
                          <Lock className="w-6 h-6 text-gray-400" />
                        ) : (
                          <Icon className="w-6 h-6 text-blue-600" />
                        )}
                      </div>

                      {/* Lesson Info */}
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm text-gray-500">Lesson {index + 1}</span>
                          {lesson.isPreview && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">
                              Preview
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900">{lesson.title}</h3>
                        {lesson.description && (
                                  <div className="text-gray-600 text-sm mb-2 line-clamp-2">
                                    <MathRenderer 
                                      content={lesson.description}
                                      enableMarkdown={true}
                                      enableMath={true}
                                      className="text-sm text-gray-600"
                                    />
                                  </div>
                                )}
                      </div>

                      {/* Duration */}
                      {lesson.duration && (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{lesson.duration}m</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Instructor */}
            {course.instructor && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="font-bold text-gray-900 mb-4">Instructor</h3>
                <div className="flex items-center gap-3 mb-3">
                  {course.instructor.avatar ? (
                    <img 
                      src={course.instructor.avatar}
                      alt={course.instructor.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-gray-900">{course.instructor.name}</div>
                    <div className="text-sm text-gray-600">{course.instructor.title}</div>
                  </div>
                </div>
                {course.instructor.bio && (
                  <p className="text-sm text-gray-600">{course.instructor.bio}</p>
                )}
              </div>
            )}

            {/* Course Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Course Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Level</span>
                  <span className="font-medium text-gray-900">{course.level}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Language</span>
                  <span className="font-medium text-gray-900">{course.language || 'English'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Category</span>
                  <span className="font-medium text-gray-900">{course.category}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Students</span>
                  <span className="font-medium text-gray-900">{course.enrollmentCount || 0}</span>
                </div>
                {course.certificate && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Certificate</span>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="space-y-3">
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  <Heart className="w-5 h-5" />
                  <span>Save for Later</span>
                </button>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  <Share2 className="w-5 h-5" />
                  <span>Share Course</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};